import MuxVideo from '@mux/mux-video-react';
import {
  Content,
  KeyTextField,
  NumberField,
  RichTextField
} from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import { VideoSliceDefaultPrimary } from 'components/../../prismicio-types';
import PrismicInlineText from 'components/PRISMIC/PrismicInlineText';
import styles from 'components/PRISMIC/scss/pages.module.scss';
import { PageBuilderHeading } from 'components/PRISMIC/slices/Heading/Heading';
import { useEffect, useState } from 'react';

import type { JSX } from 'react';

/**
 * Props for `Video`.
 */
export type VideoProps = SliceComponentProps<Content.VideoSlice>;

const WebVideoPlayer = ({
  scale,
  aspectRatio,
  height,
  width,
  description,
  videoId,
  posterUrl
}: VideoSliceDefaultPrimary) => {
  const aspectPercentage = aspectRatio === '16:9' ? 56.25 : 75;
  const widthFromHeight = (height! / aspectPercentage) * 100;
  const maxWidth = Math.max(width!, widthFromHeight);

  return (
    <div className={cx(styles['pages-module'], styles['pages-jwplayer'])}>
      <div
        className={styles['pages-jwplayer__wrapper']}
        style={{
          width: `${scale! < 0 ? 100 : scale}%`,
          maxWidth
        }}
      >
        <div
          className={styles['pages-jwplayer__container']}
          style={{ paddingBottom: `${aspectPercentage}%` }}
        >
          <div className={styles['pages-jwplayer__player']}>
            <MuxVideo src={videoId || ''} controls poster={posterUrl || ''} />
          </div>
        </div>
      </div>
      <p className={styles['pages-jwplayer__description']}>
        <PrismicInlineText field={description} />
      </p>
    </div>
  );
};

const EmbeddedVideoContainer = ({
  children,
  description
}: {
  children: React.ReactNode;
  description: RichTextField;
}): JSX.Element => (
  <div className={cx(styles['pages-module'], styles['pages-video-embed'])}>
    <p className={cx(styles['pages-video-embed__description'], styles.copy)}>
      <PrismicInlineText field={description} />
    </p>
    {children}
  </div>
);

const parseLivestreamUrl = (videoId: KeyTextField, width: NumberField) => {
  const embedPath = videoId!.toString().split('?')[0];
  const endWidth = width! > 0 ? width : 560;
  const queryParams = {
    autoplay: false,
    height: Math.floor(endWidth! * 0.5625),
    width: endWidth,
    mute: false
  };
  // @ts-ignore
  return `${embedPath}?${new URLSearchParams(queryParams).toString()}`;
};

const useYouTubeDomain = () => {
  const [youtubeDomain, setYoutubeDomain] = useState(
    'www.youtube-nocookie.com'
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleConsent = () => {
      const hasTargetingConsent = window.__userHasConsentedToTargetingCookies;
      setYoutubeDomain(
        hasTargetingConsent ? 'www.youtube.com' : 'www.youtube-nocookie.com'
      );
    };

    handleConsent();
    window.addEventListener('OnetrustActiveGroupsLoaded', handleConsent);
    return () => {
      window.removeEventListener('OnetrustActiveGroupsLoaded', handleConsent);
    };
  }, []);

  return youtubeDomain;
};

/**
 * Component for "Video" Slices. Most logic is directly brought from Roadrunner.
 */
const Video = ({ slice }: VideoProps): JSX.Element => {
  const VideoComponent = () => {
    switch (slice.variation) {
      case 'default':
        return WebVideoPlayer(slice.primary);
      case 'tedTalk':
        return (
          <EmbeddedVideoContainer description={slice.primary.description}>
            <div
              style={{
                width: '100%',
                maxWidth: slice.primary.width || undefined
              }}
            >
              <div
                className={styles['pages-video-embed__video']}
                style={{
                  paddingBottom: `${
                    slice.primary.width! > 0
                      ? (slice.primary.height! / slice.primary.width!) * 100
                      : 56.25
                  }%`
                }}
              >
                <div>
                  <iframe
                    className={cx(
                      styles['pages-video-embed__video__object'],
                      styles['pages-video-embed__iframe']
                    )}
                    title="TED Talk embedded player"
                    src={`https://embed.ted.com/talks/${slice.primary.videoId}`}
                    allowFullScreen={slice.primary.allowFullscreen}
                  />
                </div>
              </div>
            </div>
          </EmbeddedVideoContainer>
        );
      case 'vimeo':
        return (
          <EmbeddedVideoContainer description={slice.primary.description}>
            <div
              style={{
                maxWidth: slice.primary.width! > 0 ? slice.primary.width! : 560
              }}
            >
              <div className={styles['pages-video-embed__video']}>
                <iframe
                  className={cx(styles['pages-video-embed__video__object'])}
                  title="Vimeo embedded player"
                  src={`//player.vimeo.com/video/${slice.primary.videoId}`}
                  allowFullScreen={slice.primary.allowFullscreen}
                />
              </div>
            </div>
          </EmbeddedVideoContainer>
        );
      case 'youtube':
        const youtubeDomain = useYouTubeDomain();
        return (
          <EmbeddedVideoContainer description={slice.primary.description}>
            <div
              style={{
                maxWidth: slice.primary.width! > 0 ? slice.primary.width! : 560
              }}
            >
              <div className={styles['pages-video-embed__video']}>
                <iframe
                  className={cx(styles['pages-video-embed__video__object'])}
                  title="Youtube embedded player"
                  src={`//${youtubeDomain}/embed/${slice.primary.videoId}`}
                  allowFullScreen={slice.primary.allowFullscreen}
                />
              </div>
            </div>
          </EmbeddedVideoContainer>
        );
      case 'livestream':
        return (
          <EmbeddedVideoContainer description={slice.primary.description}>
            <div
              style={{
                maxWidth: slice.primary.width! > 0 ? slice.primary.width! : 560
              }}
            >
              <div className={styles['pages-video-embed__video']}>
                <iframe
                  className={cx(styles['pages-video-embed__video__object'])}
                  title="Livestream embedded player"
                  src={parseLivestreamUrl(
                    slice.primary.videoId,
                    slice.primary.width
                  )}
                />
              </div>
            </div>
          </EmbeddedVideoContainer>
        );
      default:
        return null;
    }
  };
  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <PageBuilderHeading level="default">
        <PrismicRichText field={slice.primary.heading} />
      </PageBuilderHeading>
      <VideoComponent />
    </div>
  );
};

export default Video;
